"use client";

import React from "react";
import { ShieldCheck, Cpu, Activity, BadgeCheck, Info, Lock } from "lucide-react";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const protocols = [{
  id: 1,
  title: "Hard Water Removal",
  description: "Advanced chemical restoration for mineral-stained glass surfaces.",
  techDetails: "We utilize professional-grade acidic solutions and specialized scrapers to dissolve calcium and magnesium deposits without etching the glass substrate.",
  icon: ShieldCheck
}, {
  id: 2,
  title: "Precision Squeegee",
  description: "Streak-free finish using industry-leading rubber blades.",
  techDetails: "Our technicians employ high-grade brass squeegees with replaceable rubber edges, ensuring a perfect seal and 100% moisture removal in a single pass.",
  icon: Cpu
}, {
  id: 3,
  title: "Eco-Friendly Soaps",
  description: "Biodegradable formulas safe for your home and landscaping.",
  techDetails: "Our proprietary cleaning solution is pH-balanced and phosphate-free, designed to lift particulates while leaving a protective, dust-repellent microscopic film.",
  icon: Activity
}, {
  id: 4,
  title: "Seasonal Maintenance",
  description: "Proactive care to prevent long-term glass degradation.",
  techDetails: "Scheduled deep cleans include frame detailing and track lubrication, preventing the buildup of corrosive environmental pollutants and organic debris.",
  icon: BadgeCheck
}];
export default function SpecializedSolutions() {
  return <section data-section-id="246305" id="specialized-solutions" className="bg-background text-foreground relative">
      {}
      <div className="bg-popover py-24 px-4 sm:px-6 lg:px-8 border-y border-border/30">
        <div className="max-w-7xl mx-auto">
          
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center p-2 mb-4 rounded-md bg-primary/5 border border-primary/10">
              <Lock className="w-5 h-5 text-primary/70 mr-2" />
              <span className="text-sm font-default font-semibold text-primary/80 tracking-widest uppercase">Technical Precision</span>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold mb-6 tracking-tighter">
              Specialized <span className="text-transparent bg-clip-text bg-gradient-to-r from-foreground to-muted-foreground">Cleaning Solutions</span>
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto text-lg font-default">
              6SPC utilizes elite tools and eco-friendly chemistry to achieve a luxury-level shine that standard methods simply cannot match.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {protocols.map((protocol, i) => {
            const Icon = protocol.icon;
            return <div key={protocol.id} data-index={i} className="h-full">
                  <HoverCard>
                    <HoverCardTrigger asChild>
                      <div className="group relative h-full p-8 rounded-md bg-card/40 border border-border/50 backdrop-blur-md hover:bg-card/60 hover:border-primary/40 transition-all duration-500 cursor-help overflow-hidden flex flex-col">
                        {}
                        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
                        
                        <div className="mb-6 relative">
                          <div className="absolute inset-0 bg-primary/10 blur-xl rounded-md opacity-0 group-hover:opacity-50 transition-opacity duration-500" />
                          <div className="relative inline-flex p-4 rounded-md bg-background/50 border border-border/50 group-hover:border-primary/30 transition-colors duration-300">
                            <Icon className="w-10 h-10 text-primary/80 drop-shadow-[0_0_8px_rgba(255,255,255,0.2)]" />
                          </div>
                        </div>
                        
                        <h3 className="text-xl font-default font-semibold mb-3 text-card-foreground group-hover:text-primary transition-colors duration-300 flex items-center justify-between">
                          {protocol.title}
                          <Info className="w-4 h-4 text-muted-foreground opacity-50 group-hover:opacity-100 transition-opacity" />
                        </h3>
                        
                        <p className="text-muted-foreground text-sm leading-relaxed flex-grow font-default">
                          {protocol.description}
                        </p>
                        
                        <div className="mt-6 pt-4 border-t border-border/30 flex items-center text-xs font-mono text-muted-foreground uppercase tracking-widest">
                          View Technical Specs
                        </div>
                      </div>
                    </HoverCardTrigger>
                    <HoverCardContent side="bottom" align="start" className="w-80 bg-background/95 backdrop-blur-xl border-primary/20 shadow-2xl shadow-black/50 p-5 rounded-md">
                      <div className="space-y-3">
                        <div className="flex items-center space-x-2 text-primary/80 mb-2">
                          <Cpu className="w-4 h-4" />
                          <span className="font-mono text-xs font-bold uppercase tracking-widest">Methodology</span>
                        </div>
                        <p className="text-sm text-muted-foreground leading-relaxed font-default">
                          {protocol.techDetails}
                        </p>
                        <div className="pt-3 mt-3 border-t border-border/50 flex items-center justify-between text-[10px] font-mono text-muted-foreground">
                          <span>GRADE: <span className="text-primary">PREMIUM</span></span>
                          <span>SAFETY: CERTIFIED</span>
                        </div>
                      </div>
                    </HoverCardContent>
                  </HoverCard>
                </div>;
          })}
          </div>

          <div className="mt-16 text-center">
            <Button variant="outline" size="lg" className="border-primary/30 text-primary hover:bg-primary/10 hover:text-primary-foreground transition-all duration-300 rounded-md font-default" asChild>
              <Link to="/ExpertisePage">
                Explore Our Full Technical Process
              </Link>
            </Button>
          </div>

        </div>
      </div>
    </section>;
}